export const PAYMENT_STATUSES = [
  'PENDING',
  'CONFIRMED',
  'FAILED',
  'EXPIRED',
  'REFUNDED',
  'CHARGEBACKED',
  'USED',
] as const;

export type PaymentStatus = (typeof PAYMENT_STATUSES)[number];

/**
 * Stake payment session statuses from unified tournament payments backend.
 * These are the statuses returned by POST /tournaments/:id/payment-sessions/join
 * and the paymentSessionUpdated socket event.
 */
export const STAKE_SESSION_STATUSES = [
  'PENDING',
  'UNDERPAID',
  'CONFIRMED_EXACT',
  'OVERPAID',
  'TIMEOUT',
  'REFUND_INITIATED',
  'REFUNDED',
  'REFUND_FAILED',
] as const;

export type StakeSessionStatus = (typeof STAKE_SESSION_STATUSES)[number];

export type StakeSessionType = 'CREATE' | 'JOIN';

/**
 * Stake payment session from unified tournament payments.
 * Returned by POST /tournaments/:id/payment-sessions/join
 * and paymentSessionUpdated socket event.
 */
export type SessionState = 'new' | 'reused' | 'already_confirmed';

export interface StakePaymentSession {
  id: number;
  status: StakeSessionStatus;
  sessionType: StakeSessionType;
  expectedAmount: number;
  paidAmount: number;
  paymentDeadline: string;
  paymentRequired: boolean;
  depositAddress?: string;
  tournament?: number;
  sessionState?: SessionState;
  alreadyJoined?: boolean;

  // NEW — optional Solana chain context (POL responses omit these fields)
  stakeCurrency?: {
    shortName: string;               // 'POL' | 'SOL'
    chain: 'polygon' | 'solana';
    precision: number;
  };
  networkInfo?: {
    chain: 'polygon' | 'solana';
    network: string;                 // 'polygon' | 'solana-devnet' | 'solana-mainnet-beta'
    commitmentLevel?: 'confirmed' | 'finalized' | null;
  };
}

/**
 * Response from POST /tournaments/:id/payment-sessions/join
 */
export interface CreateStakeSessionResponse {
  data: StakePaymentSession;
}

/**
 * Payload from paymentSessionUpdated socket event.
 *
 * Backend `emitPaymentSessionUpdate` populates `tournament.stake_currency`
 * before assembling the payload, so chain context is shipped over the socket
 * channel and not just over REST. Older backends omit these fields — they're
 * optional for forward/backward compatibility.
 */
export interface PaymentSessionUpdatedPayload {
  paymentSession: {
    id: number;
    status: StakeSessionStatus;
    session_type: StakeSessionType;
    expected_amount: number;
    paid_amount: number;
    payment_deadline: string;
    tournament: number;
    stake_currency?: {
      short_name: string;
      chain: 'polygon' | 'solana';
      precision: number;
    } | null;
    network_info?: {
      chain: 'polygon' | 'solana';
      network: string;
      commitment_level?: 'confirmed' | 'finalized' | null;
    } | null;
  };
}

export const PAYMENT_TYPES = [
  'TOURNAMENT_CREATE_ENTRY',
  'TOURNAMENT_JOIN_ENTRY',
] as const;

export type PaymentType = (typeof PAYMENT_TYPES)[number];

export const isPaymentStatus = (value: unknown): value is PaymentStatus =>
  typeof value === 'string' && (PAYMENT_STATUSES as readonly string[]).includes(value);

export const isPaymentType = (value: unknown): value is PaymentType =>
  typeof value === 'string' && (PAYMENT_TYPES as readonly string[]).includes(value);

export interface PaymentProviderData {
  toAddress: string;
  network: string;
  chainId: number | null;     // CHANGED: nullable — null for Solana (no EVM chain ID)
  tokenSymbol: string;
  qrCodeUrl?: string;
  commitmentLevel?: string | null;  // NEW — Solana commitment level ('confirmed'|'finalized'), null for POL
}

export interface Payment {
  id: string;
  status: PaymentStatus;
  type: PaymentType;
  tournamentId: string;
  amount: string;
  currency: string;
  provider: string;
  expiresAt: string;
  providerData: PaymentProviderData;
}

export interface CreatePaymentSessionRequest {
  provider: string;
  returnUrl: string;
  cancelUrl: string;
}

export interface CreatePaymentSessionResponse {
  payment: Payment;
}

/**
 * UI states for payment flows as specified in PRD.
 * These represent the frontend's interpretation of backend payment statuses and error codes.
 */
export enum PaymentUIState {
  UI_PAYMENT_NOT_REQUIRED = 'UI_PAYMENT_NOT_REQUIRED',
  UI_PAYMENT_REQUIRED = 'UI_PAYMENT_REQUIRED',
  UI_PAYMENT_IN_PROGRESS = 'UI_PAYMENT_IN_PROGRESS',
  UI_PAYMENT_EXPIRED = 'UI_PAYMENT_EXPIRED',
  UI_PAYMENT_FAILED = 'UI_PAYMENT_FAILED',
  UI_JOIN_NOT_ALLOWED = 'UI_JOIN_NOT_ALLOWED',
}

/**
 * Represents API error responses from payment endpoints.
 */
export interface PaymentError {
  code: string;
  message: string;
  details?: string;
  httpStatus?: number;
}

/**
 * Input for mapping payment status and error context to UI state.
 */
export interface PaymentToUIStateInput {
  /** Current payment status from backend */
  status: PaymentStatus;
  /** Optional HTTP status from API response */
  httpStatus?: number;
  /** Optional error code from API response */
  errorCode?: string;
  /** Whether tournament has entry fee (affects NOT_REQUIRED state) */
  hasEntryFee?: boolean;
}

/**
 * State for PaymentContext to be used across components.
 */
export interface PaymentContextState {
  /** Current payment object if available */
  currentPayment?: Payment;
  /** Current UI state derived from payment status and errors */
  paymentUIState: PaymentUIState;
  /** Whether a payment operation is in progress */
  isLoading: boolean;
  /** Any error that occurred during payment operations */
  error?: PaymentError;
}

/**
 * Maps payment status and error context to UI state according to PRD rules.
 * 
 * @param input - Payment status, HTTP status, error code, and entry fee context
 * @returns The appropriate UI state for displaying to users
 */
export const mapPaymentToUIState = (input: PaymentToUIStateInput): PaymentUIState => {
  const { status, httpStatus, errorCode, hasEntryFee } = input;

  // Handle specific error code mappings from PRD
  if (httpStatus === 402 && errorCode === 'ENTRY_FEE_PAYMENT_REQUIRED') {
    return PaymentUIState.UI_PAYMENT_REQUIRED;
  }

  if (httpStatus === 409 && errorCode === 'ENTRY_FEE_PAYMENT_PENDING') {
    return PaymentUIState.UI_PAYMENT_IN_PROGRESS;
  }

  if (httpStatus === 410 && errorCode === 'ENTRY_FEE_PAYMENT_EXPIRED') {
    return PaymentUIState.UI_PAYMENT_EXPIRED;
  }

  // Solana-specific error codes (from data-model-contract §7)
  if (errorCode === 'SOLANA_INSUFFICIENT_CONFIRMATIONS') {
    // TX not yet at required commitment level — treat as pending, will retry
    return PaymentUIState.UI_PAYMENT_IN_PROGRESS;
  }

  if (errorCode === 'SOLANA_AMOUNT_MISMATCH') {
    // Deposited amount ≠ expected — maps to same UI as existing POL payment failure
    return PaymentUIState.UI_PAYMENT_FAILED;
  }

  if (errorCode === 'SOLANA_UNRECOGNIZED_DEPOSITOR_ADDRESS') {
    // Deposit came from a different wallet than declared — payment failed
    return PaymentUIState.UI_PAYMENT_FAILED;
  }

  // SOLANA_ADMIN_HOT_WALLET_INSUFFICIENT_SOL: 500 — admin alert, surface as generic failure
  if (errorCode === 'SOLANA_ADMIN_HOT_WALLET_INSUFFICIENT_SOL') {
    return PaymentUIState.UI_PAYMENT_FAILED;
  }

  // Handle various payment failure error codes
  const paymentFailureCodes = [
    'ENTRY_FEE_PAYMENT_MISMATCH',
    'ENTRY_FEE_PAYMENT_ALREADY_USED',
    'ENTRY_FEE_PAYMENT_REFUNDED',
    'ENTRY_FEE_PAYMENT_CHARGEBACKED',
    'PAYMENT_OWNER_MISMATCH',
  ];

  if (paymentFailureCodes.includes(errorCode || '')) {
    return PaymentUIState.UI_PAYMENT_FAILED;
  }

  // Handle tournament access restrictions
  const tournamentRestrictionCodes = [
    'TOURNAMENT_FULL',
    'TOURNAMENT_ALREADY_STARTED',
    'JOIN_NOT_ALLOWED',
  ];

  if (tournamentRestrictionCodes.includes(errorCode || '')) {
    return PaymentUIState.UI_JOIN_NOT_ALLOWED;
  }

  // Handle payment status-based mapping
  switch (status) {
    case 'PENDING':
      return PaymentUIState.UI_PAYMENT_IN_PROGRESS;
    
    case 'CONFIRMED':
      // If payment is confirmed, no payment is required for this flow
      return PaymentUIState.UI_PAYMENT_NOT_REQUIRED;
    
    case 'EXPIRED':
      return PaymentUIState.UI_PAYMENT_EXPIRED;
    
    case 'FAILED':
    case 'REFUNDED':
    case 'CHARGEBACKED':
      return PaymentUIState.UI_PAYMENT_FAILED;
    
    case 'USED':
      // Payment already used for another tournament/join
      return PaymentUIState.UI_PAYMENT_FAILED;
    
    default:
      // Default case: check if tournament has entry fee
      return hasEntryFee ? 
        PaymentUIState.UI_PAYMENT_REQUIRED : 
        PaymentUIState.UI_PAYMENT_NOT_REQUIRED;
  }
};

/**
 * Type guard function for PaymentUIState enum values.
 */
export const isPaymentUIState = (value: unknown): value is PaymentUIState =>
  typeof value === 'string' && Object.values(PaymentUIState).includes(value as PaymentUIState);

/**
 * Available payment providers as specified in PRD.
 */
export const PAYMENT_PROVIDERS = ['ONCHAIN_POLYGON', 'ONCHAIN_SOLANA'] as const;

/**
 * Type for supported payment providers.
 */
export type PaymentProvider = (typeof PAYMENT_PROVIDERS)[number];

/**
 * Available payment currencies as specified in PRD.
 */
export const PAYMENT_CURRENCIES = ['POL', 'SOL'] as const;

/**
 * Type for supported payment currencies.
 */
export type PaymentCurrency = (typeof PAYMENT_CURRENCIES)[number];

/**
 * API error format returned from payment endpoints.
 */
export interface PaymentApiError {
  /** Error code used to identify specific error conditions */
  code: string;
  /** Human-readable error message */
  message: string;
  /** Optional details about the error */
  details?: unknown;
}

/**
 * Response type for GET /payments/:paymentId endpoint.
 */
export interface GetPaymentStatusResponse {
  /** Payment details */
  id: string;
  status: PaymentStatus;
  type: PaymentType;
  tournamentId: string;
  amount?: string;
  currency?: string;
  provider?: string;
  expiresAt?: string;
  providerData?: PaymentProviderData;
  /** User ID who owns this payment (for ownership validation) */
  userId?: string;
}

/**
 * Request type for POST /tournaments/:id/publish endpoint.
 */
export interface PublishTournamentRequest {
  /** ID of the confirmed payment for tournament creation */
  paymentId: string;
}

/**
 * Response type for POST /tournaments/:id/publish endpoint.
 */
export interface PublishTournamentResponse {
  /** Tournament ID */
  id: string;
  /** Tournament status after publish operation */
  status: string;
}

/**
 * Request type for POST /tournaments/:id/join endpoint.
 */
export interface JoinTournamentRequest {
  /** ID of the confirmed payment (only for paid tournaments) */
  paymentId?: string;
}

/**
 * Response type for POST /tournaments/:id/join endpoint.
 */
export interface JoinTournamentResponse {
  /** Tournament ID */
  tournamentId: string;
  /** Join status */
  status: string;
}
